import React, { useState, useEffect, useMemo, useCallback } from "react";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import {
  Box,
  Paper,
  TextField,
  Button,
  Tabs,
  Tab,
  MenuItem,
  Switch,
  FormControlLabel,
  CircularProgress,
  Chip,
  useMediaQuery,
} from "@material-ui/core";
import {
  SaveRounded as SaveIcon,
  CheckCircleRounded as PublishedIcon,
  RadioButtonUncheckedRounded as DraftIcon,
} from "@material-ui/icons";
import { toast } from "react-toastify";

import { i18n } from "../../../translate/i18n";
import {
  listPages,
  savePage,
  setPageStatus,
} from "../../../services/api/siteContent";

// ───── Design tokens — verbatim from Admin/index.js (single source of truth)
const SP = {
  xxs: 4, xs: 8, sm: 12, md: 16, lg: 20, xl: 24, xxl: 32, hero: 48,
};
const TT = { min: 44, primary: 48, hero: 56 };
const TYPE = {
  caption:    { fontSize: 12, lineHeight: 1.4, letterSpacing: 0.2 },
  body:       { fontSize: 14, lineHeight: 1.5 },
  bodyStrong: { fontSize: 15, lineHeight: 1.45, fontWeight: 500 },
  subheading: { fontSize: 17, lineHeight: 1.4,  fontWeight: 500 },
  heading:    { fontSize: 20, lineHeight: 1.3,  fontWeight: 600 },
};
const RAD = { pill: 999, sm: 8, md: 12, lg: 16, xl: 20 };
const COL = {
  brandGreen:     "#25d366",
  brandGreenDeep: "#128c7e",
  brandGreenSoft: "rgba(37,211,102,0.08)",
  textPrimary:    "#0B0D0C",
  textSecondary:  "#52525b",
  borderSoft:     "#eaeaea",
  pageBg:         "#f7faf8",
  cardBg:         "#ffffff",
  destructive:    "#ef4444",
  warning:        "#f59e0b",
};

const LANGUAGES = [
  { code: "he", label: "עברית", dir: "rtl" },
  { code: "en", label: "English", dir: "ltr" },
  { code: "ru", label: "Русский", dir: "ltr" },
  { code: "ar", label: "العربية", dir: "rtl" },
  { code: "es", label: "Español", dir: "ltr" },
];

const META_TITLE_MAX = 70;
const META_DESC_MAX  = 160;

// Default-empty translation shape — keeps the form controlled even before the
// API answers. Mirrors the backend SavePage payload so a fresh slug can be
// authored end-to-end without first hitting the GET.
const EMPTY_TRANSLATION = {
  title: "",
  metaTitle: "",
  metaDescription: "",
  body: "",
  contentBlocks: {},
};

const useStyles = makeStyles((theme) => {
  // Dark-mode aware tokens — match Tags/Campaigns/Contacts CRM language.
  const isDark     = theme.palette.type === "dark";
  const accent     = isDark ? "#2ee37a" : "#128c7e";
  const accentSoft = isDark ? "rgba(46,227,122,0.08)" : "rgba(18,140,126,0.06)";
  const hair       = isDark ? "rgba(255,255,255,0.08)" : "rgba(15,22,20,0.06)";
  const fg         = isDark ? "rgba(255,255,255,0.92)" : "#0f1614";
  const fgMuted    = isDark ? "rgba(255,255,255,0.55)" : "rgba(15,22,20,0.55)";
  const surfaceBg  = isDark ? "#13181a" : "#ffffff";

  return {
    root: {
      display: "flex",
      flexDirection: "column",
      gap: SP.md,
      fontFamily: "'Roboto',system-ui,-apple-system,sans-serif",
      color: fg,
      width: "100%",
      maxWidth: "100%",
      minWidth: 0,
      boxSizing: "border-box",
      // Sticky save bar lifts content off the bottom edge on mobile so the
      // last textarea isn't covered by the CTA chrome.
      [theme.breakpoints.down("xs")]: {
        paddingBottom: 96,
        "& .MuiInputBase-input": { fontSize: 16 },
        "& .MuiInputLabel-root": { fontSize: 13 },
        "& .MuiFormHelperText-root": { fontSize: 11 },
      },
    },

    // ── Section label (CRM accent meta — uppercase 10.5/0.22em #128c7e) ──
    accentMeta: {
      fontSize: 10.5,
      fontWeight: 700,
      letterSpacing: "0.22em",
      textTransform: "uppercase",
      color: accent,
      lineHeight: 1.2,
    },
    sectionHead: {
      display: "flex",
      alignItems: "center",
      gap: SP.sm,
      marginBottom: SP.md,
      paddingBottom: SP.sm,
      borderBottom: `1px solid ${hair}`,
    },
    sectionTitle: {
      ...TYPE.subheading,
      color: fg,
      margin: 0,
    },

    // Header row hosts the page select + publish toggle. On mobile the
    // toggle reflows under the dropdown with comfortable touch zone.
    headerRow: {
      display: "flex",
      flexWrap: "wrap",
      alignItems: "center",
      gap: SP.md,
      minWidth: 0,
      [theme.breakpoints.down("xs")]: {
        flexDirection: "column",
        alignItems: "stretch",
        gap: SP.sm,
      },
    },
    pageSelect: {
      minWidth: 280,
      order: -1,
      [theme.breakpoints.down("sm")]: {
        width: "100%",
        minWidth: 0,
      },
    },
    publishToggle: {
      marginInlineStart: "auto",
      [theme.breakpoints.down("xs")]: {
        marginInlineStart: 0,
        minHeight: TT.min,
        display: "flex",
        alignItems: "center",
        "& .MuiFormControlLabel-root": {
          minHeight: TT.min,
          marginInlineStart: 0,
        },
      },
    },

    // ── Card surface — CRM Paper treatment (radius 18, hairline border,
    //    soft shadow). Replaces previous flat outlined box.
    // xs override (<360px) per Linear-dense spec: tighter padding (10/12),
    // 8px radius, hairline-only border, NO resting shadow, 6px margin.
    card: {
      position: "relative",
      background: surfaceBg,
      border: `1px solid ${hair}`,
      borderRadius: 18,
      padding: SP.lg,
      minWidth: 0,
      maxWidth: "100%",
      boxSizing: "border-box",
      overflow: "hidden",
      boxShadow: isDark
        ? "0 1px 0 rgba(255,255,255,0.04) inset, 0 8px 24px rgba(0,0,0,0.32)"
        : "0 1px 0 rgba(15,22,20,0.02), 0 8px 24px rgba(15,22,20,0.04)",
      [theme.breakpoints.down("xs")]: {
        padding: "10px 12px",
        borderRadius: 8,
        border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "#ededed"}`,
        boxShadow: "none",
        marginBottom: 6,
      },
    },
    // Page-status stripe sits flush with the card top edge (#25d366 / #f59e0b
    // / #ef4444). Color comes from inline style so we don't compile a class
    // per status. On xs the stripe shrinks to a 3px hairline aligned to the
    // 8px card radius, keeping the Linear-dense aesthetic.
    cardStripe: {
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      height: 4,
      background: COL.brandGreen,
      borderTopLeftRadius: 18,
      borderTopRightRadius: 18,
      [theme.breakpoints.down("xs")]: {
        height: 3,
        borderTopLeftRadius: 8,
        borderTopRightRadius: 8,
      },
    },
    cardStripeOffset: {
      paddingTop: SP.lg + 4,
      [theme.breakpoints.down("xs")]: {
        paddingTop: 10 + 3,
      },
    },

    // ── Pages list summary (above-tabs) — cardStackMode v3 mini-rail showing
    //    page name + slug + last-edited timestamp.
    pageListSummary: {
      display: "flex",
      flexDirection: "column",
      gap: 4,
      minWidth: 0,
      flex: 1,
    },
    pageListName: {
      fontSize: 15,
      fontWeight: 500,
      color: fg,
      lineHeight: 1.3,
      display: "-webkit-box",
      WebkitLineClamp: 2,
      WebkitBoxOrient: "vertical",
      overflow: "hidden",
      textOverflow: "ellipsis",
      wordBreak: "break-word",
      overflowWrap: "anywhere",
      // xs: drop to title token (13/17 weight 600) per Linear-dense scale.
      [theme.breakpoints.down("xs")]: {
        fontSize: 13,
        fontWeight: 600,
        lineHeight: "17px",
        letterSpacing: "-0.005em",
        color: isDark ? "rgba(255,255,255,0.92)" : "#0a0a0a",
      },
    },
    pageListSlug: {
      fontSize: 10.5,
      fontWeight: 700,
      letterSpacing: "0.22em",
      textTransform: "uppercase",
      color: accent,
      lineHeight: 1.2,
      // xs: caption token — 10/12, 0.04em, still uppercase (caption only).
      [theme.breakpoints.down("xs")]: {
        fontSize: 10,
        fontWeight: 500,
        lineHeight: "12px",
        letterSpacing: "0.04em",
      },
    },
    pageListTimestamp: {
      fontFamily: "'Roboto',system-ui,-apple-system,sans-serif",
      fontSize: 12,
      fontWeight: 400,
      color: fgMuted,
      letterSpacing: 0,
      lineHeight: 1.4,
      textAlign: "end",
      flexShrink: 0,
      fontFeatureSettings: '"tnum" 1',
      fontVariantNumeric: "tabular-nums",
      // xs: meta-row token — 11/14 #a3a3a3-equivalent (fgMuted preserved
      // for dark-mode parity).
      [theme.breakpoints.down("xs")]: {
        fontSize: 11,
        lineHeight: "14px",
        color: isDark ? "rgba(255,255,255,0.45)" : "#a3a3a3",
      },
    },
    pageListBar: {
      display: "flex",
      alignItems: "center",
      gap: SP.md,
      paddingBlock: SP.sm,
      paddingInline: SP.md,
      marginBottom: SP.md,
      background: accentSoft,
      borderRadius: RAD.md,
      border: `1px solid ${hair}`,
      // xs override: dense outer (10/12 padding, 8 radius, hairline border,
      // no accent wash so the card surface stays neutral per anti_pattern
      // no_accent_on_chrome — accent reserved for status pill + CTA only).
      [theme.breakpoints.down("xs")]: {
        gap: 8,
        paddingBlock: 7,
        paddingInline: 10,
        marginBottom: 6,
        borderRadius: 8,
        background: "transparent",
        border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "#ededed"}`,
      },
    },

    form: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: SP.md,
      minWidth: 0,
      "& > *": {
        minWidth: 0,
        maxWidth: "100%",
        boxSizing: "border-box",
      },
      [theme.breakpoints.down("sm")]: {
        gridTemplateColumns: "1fr",
      },
    },
    fullRow: {
      gridColumn: "1 / -1",
      minWidth: 0,
      maxWidth: "100%",
    },
    helper: {
      ...TYPE.caption,
      color: fgMuted,
      marginTop: SP.xxs,
    },

    // ── TextField surfaces — refined input chrome matching CRM hairline
    //    aesthetic. Replaces MUI default thick outline with hairline border
    //    + accent focus.
    inputSurface: {
      "& .MuiOutlinedInput-root": {
        background: isDark ? "rgba(255,255,255,0.02)" : "rgba(15,22,20,0.015)",
        borderRadius: RAD.md,
        transition: "background 0.14s ease, border-color 0.14s ease",
        "& fieldset": {
          borderColor: hair,
          borderWidth: 1,
        },
        "&:hover fieldset": {
          borderColor: isDark ? "rgba(255,255,255,0.16)" : "rgba(15,22,20,0.12)",
        },
        "&.Mui-focused": {
          background: accentSoft,
          "& fieldset": {
            borderColor: accent,
            borderWidth: 1,
          },
        },
      },
      "& .MuiInputLabel-root.Mui-focused": {
        color: accent,
      },
    },

    bodyField: {
      "& .MuiInputBase-inputMultiline": {
        minHeight: 200,
      },
      [theme.breakpoints.down("xs")]: {
        "& .MuiInputBase-inputMultiline": {
          minHeight: 160,
        },
      },
    },
    pageMenuItem: {
      [theme.breakpoints.down("xs")]: {
        paddingTop: SP.xs,
        paddingBottom: SP.xs,
        fontSize: 13,
        "& .MuiChip-root": {
          height: 20,
          fontSize: 11,
        },
        "& .MuiChip-label": {
          paddingInlineStart: 6,
          paddingInlineEnd: 6,
        },
      },
    },

    // ── Sticky bottom action bar — WhatsApp-green primary CTA pinned to
    //    the viewport on mobile, inline at the end of the form on desktop.
    saveRow: {
      display: "flex",
      justifyContent: "flex-end",
      alignItems: "center",
      gap: SP.sm,
      marginTop: SP.lg,
      paddingTop: SP.md,
      borderTop: `1px solid ${hair}`,
      [theme.breakpoints.down("xs")]: {
        position: "fixed",
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 12,
        flexDirection: "column",
        gap: SP.xs,
        margin: 0,
        padding: `${SP.sm}px ${SP.md}px`,
        background: surfaceBg,
        borderTop: `1px solid ${hair}`,
        boxShadow: isDark
          ? "0 -8px 24px rgba(0,0,0,0.32)"
          : "0 -8px 24px rgba(15,22,20,0.06)",
      },
    },
    saveBtn: {
      background: COL.brandGreen,
      color: "#fff",
      fontWeight: 700,
      textTransform: "none",
      borderRadius: RAD.pill,
      padding: `${SP.sm}px ${SP.xl}px`,
      minHeight: TT.primary,
      letterSpacing: 0.2,
      boxShadow: "0 6px 16px rgba(37,211,102,0.28)",
      transition: "background 0.14s ease, box-shadow 0.14s ease, transform 0.14s ease",
      "&:hover": {
        background: COL.brandGreenDeep,
        boxShadow: "0 8px 20px rgba(18,140,126,0.32)",
      },
      "&:active": {
        transform: "translateY(1px)",
      },
      "&.Mui-disabled": {
        background: isDark ? "rgba(255,255,255,0.08)" : "rgba(15,22,20,0.08)",
        color: fgMuted,
        boxShadow: "none",
      },
      [theme.breakpoints.down("xs")]: {
        width: "100%",
        minHeight: TT.primary,
      },
    },

    loadingWrap: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: SP.xxl,
      color: fgMuted,
    },

    // ── Language tabs — accent-tinted with brand-green indicator.
    langTabs: {
      borderBottom: `1px solid ${hair}`,
      marginBottom: SP.md,
      minHeight: TT.min,
    },
    langTab: {
      ...TYPE.bodyStrong,
      textTransform: "none",
      minHeight: TT.min,
      minWidth: 0,
      padding: `${SP.xs}px ${SP.md}px`,
      color: fgMuted,
      transition: "color 0.14s ease",
      "&.Mui-selected": {
        color: accent,
      },
      "&:hover": {
        color: accent,
        background: accentSoft,
      },
      [theme.breakpoints.down("xs")]: {
        ...TYPE.body,
        minHeight: TT.primary,
        padding: `${SP.xs}px ${SP.sm}px`,
      },
    },
    tabIndicator: {
      background: accent,
      height: 3,
      borderRadius: 3,
    },
  };
});

// Inputs MUST be 16px+ on mobile to prevent iOS Safari zoom-on-focus. Applied
// via inputProps.style so it overrides MUI's 14px default cleanly.
const MOBILE_INPUT_PROPS = { style: { fontSize: 16 } };

// Defensive JSON pretty-print. Accepts null/undefined/object/array/string and
// returns a pretty JSON string. Strings that don't parse return as-is (so the
// editor doesn't silently nuke malformed authoring-in-progress content).
const prettyJson = (value) => {
  if (value === null || value === undefined) return "{}";
  if (typeof value === "string") {
    try {
      return JSON.stringify(JSON.parse(value), null, 2);
    } catch {
      return value;
    }
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return "{}";
  }
};

// Parse the JSON textarea; toast on failure. Returns `undefined` so the
// caller can decide whether to send a partial payload or abort.
const parseJsonOrToast = (raw, fieldLabel) => {
  if (!raw || !raw.trim()) return {};
  try {
    return JSON.parse(raw);
  } catch (err) {
    toast.error(
      i18n.t("siteContent.errors.invalidJson", {
        defaultValue: `Invalid JSON in ${fieldLabel}: ${err.message}`,
        field: fieldLabel,
      })
    );
    return undefined;
  }
};

// Friendly relative timestamp for the page-list bar (last edited indicator).
// Falls back to a locale date so non-recent edits still read cleanly.
const formatTimestamp = (value) => {
  if (!value) return "";
  try {
    const dt = new Date(value);
    if (isNaN(dt.getTime())) return "";
    const now = Date.now();
    const diffMs = now - dt.getTime();
    const sec = Math.floor(diffMs / 1000);
    if (sec < 60) return "just now";
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min}m ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h ago`;
    const day = Math.floor(hr / 24);
    if (day < 7) return `${day}d ago`;
    return dt.toLocaleDateString();
  } catch {
    return "";
  }
};

// Map publish state → CRM status stripe color (#25d366 published / #f59e0b
// draft / #ef4444 archived). Archived pages don't surface in the current API
// shape, but the slot is reserved here so future server flags can use it.
const stripeColorFor = (row) => {
  if (!row) return COL.borderSoft;
  if (row.archived) return COL.destructive;
  if (row.published) return COL.brandGreen;
  return COL.warning;
};

const PagesEditor = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("xs"));
  const [pages, setPages] = useState([]);
  const [selectedSlug, setSelectedSlug] = useState("");
  const [langIdx, setLangIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // Translations are kept as { [lang]: { title, metaTitle, ..., contentBlocksRaw } }
  // where contentBlocksRaw is the JSON textarea string (preserved verbatim so
  // mid-edit malformed JSON doesn't wipe the user's work).
  const [translations, setTranslations] = useState({});
  const [published, setPublished] = useState(false);

  const currentLang = LANGUAGES[langIdx]?.code || "he";

  // Initial fetch: list pages, auto-select first.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const data = await listPages();
        if (cancelled) return;
        const list = Array.isArray(data?.pages) ? data.pages : [];
        setPages(list);
        if (list.length && !selectedSlug) {
          setSelectedSlug(list[0].slug);
        }
      } catch (err) {
        toast.error(
          i18n.t("siteContent.errors.loadPages", {
            defaultValue: "Failed to load pages",
          })
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When selection changes, hydrate the form from the page row already cached
  // in `pages` (no extra round-trip — listPages returns the full payload).
  useEffect(() => {
    if (!selectedSlug) return;
    const row = pages.find((p) => p.slug === selectedSlug);
    if (!row) return;
    setPublished(Boolean(row.published));
    const nextT = {};
    for (const lang of LANGUAGES) {
      const t = (row.translations || []).find((x) => x.language === lang.code);
      nextT[lang.code] = {
        title: t?.title || "",
        metaTitle: t?.metaTitle || "",
        metaDescription: t?.metaDescription || "",
        body: t?.body || "",
        contentBlocksRaw: prettyJson(t?.contentBlocks ?? {}),
      };
    }
    setTranslations(nextT);
  }, [selectedSlug, pages]);

  const tForLang = translations[currentLang] || {
    ...EMPTY_TRANSLATION,
    contentBlocksRaw: "{}",
  };

  // Currently-selected page row — drives the status stripe + last-edited
  // timestamp in the header bar. Memoized so the lookup isn't repeated on
  // every keystroke in the translation textareas.
  const currentRow = useMemo(
    () => pages.find((p) => p.slug === selectedSlug) || null,
    [pages, selectedSlug]
  );
  const currentStripeColor = stripeColorFor(currentRow);
  const currentDisplayName =
    (currentRow &&
      ((currentRow.translations || []).find((x) => x.language === "he")?.title ||
        (currentRow.translations || []).find((x) => x.language === "en")?.title ||
        currentRow.slug)) ||
    selectedSlug ||
    "";
  const currentTimestamp = formatTimestamp(
    currentRow?.updatedAt || currentRow?.createdAt
  );

  // Partial-update helper — keep other languages untouched.
  const patchLang = useCallback(
    (lang, patch) => {
      setTranslations((prev) => ({
        ...prev,
        [lang]: { ...(prev[lang] || EMPTY_TRANSLATION), ...patch },
      }));
    },
    []
  );

  const handleSave = useCallback(async () => {
    if (!selectedSlug) return;
    // Build a translations payload, converting JSON textareas back into
    // objects. If ANY locale has invalid JSON, abort the save so we don't
    // silently zero-out contentBlocks.
    const payload = { translations: {} };
    for (const lang of LANGUAGES) {
      const t = translations[lang.code];
      if (!t) continue;
      const blocks = parseJsonOrToast(
        t.contentBlocksRaw,
        `contentBlocks (${lang.code})`
      );
      if (blocks === undefined) return;
      payload.translations[lang.code] = {
        title: t.title || "",
        metaTitle: t.metaTitle || "",
        metaDescription: t.metaDescription || "",
        body: t.body || "",
        contentBlocks: blocks,
      };
    }
    try {
      setSaving(true);
      const data = await savePage(selectedSlug, payload);
      // Refresh the row in local state so contentBlocks reflects whatever the
      // backend coerced/trimmed (e.g. meta description length clamp).
      if (data?.page) {
        setPages((prev) =>
          prev.map((p) => (p.slug === data.page.slug ? data.page : p))
        );
      }
      toast.success(
        i18n.t("siteContent.toasts.pageSaved", {
          defaultValue: "Page saved",
        })
      );
    } catch (err) {
      toast.error(
        i18n.t("siteContent.errors.savePage", {
          defaultValue: "Failed to save page",
        })
      );
    } finally {
      setSaving(false);
    }
  }, [selectedSlug, translations]);

  const handleTogglePublished = useCallback(
    async (event) => {
      const next = event.target.checked;
      const prev = published;
      setPublished(next); // optimistic
      try {
        await setPageStatus(selectedSlug, next);
        setPages((list) =>
          list.map((p) =>
            p.slug === selectedSlug ? { ...p, published: next } : p
          )
        );
        toast.success(
          next
            ? i18n.t("siteContent.toasts.published", {
                defaultValue: "Page published",
              })
            : i18n.t("siteContent.toasts.unpublished", {
                defaultValue: "Page unpublished",
              })
        );
      } catch (err) {
        setPublished(prev); // rollback
        toast.error(
          i18n.t("siteContent.errors.toggleStatus", {
            defaultValue: "Failed to update status",
          })
        );
      }
    },
    [published, selectedSlug]
  );

  if (loading) {
    return (
      <div className={classes.loadingWrap}>
        <CircularProgress size={28} style={{ color: COL.brandGreenDeep }} />
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <Paper
        elevation={0}
        className={`${classes.card} ${classes.cardStripeOffset}`}
      >
        <div
          className={classes.cardStripe}
          style={{ background: currentStripeColor }}
          aria-hidden="true"
        />

        <div className={classes.sectionHead}>
          <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 0 }}>
            <span className={classes.accentMeta}>
              {i18n.t("siteContent.section.page", { defaultValue: "PAGE" })}
            </span>
            <h2 className={classes.sectionTitle}>
              {i18n.t("siteContent.section.pageTitle", {
                defaultValue: "Page selection",
              })}
            </h2>
          </div>
        </div>

        {selectedSlug ? (
          <div className={classes.pageListBar}>
            <div className={classes.pageListSummary}>
              <span className={classes.pageListName}>{currentDisplayName}</span>
              <span className={classes.pageListSlug}>/{selectedSlug}</span>
            </div>
            {currentTimestamp ? (
              <span className={classes.pageListTimestamp}>
                {i18n.t("siteContent.lastEdited", {
                  defaultValue: "Edited",
                })}{" "}
                {currentTimestamp}
              </span>
            ) : null}
          </div>
        ) : null}

        <Box className={classes.headerRow}>
          <TextField
            select
            label={i18n.t("siteContent.pageSelect.label", {
              defaultValue: "Page",
            })}
            value={selectedSlug}
            onChange={(e) => setSelectedSlug(e.target.value)}
            variant="outlined"
            size="small"
            className={`${classes.pageSelect} ${classes.inputSurface}`}
            InputLabelProps={{ shrink: true }}
            inputProps={MOBILE_INPUT_PROPS}
          >
            {pages.length === 0 && (
              <MenuItem value="" disabled>
                {i18n.t("siteContent.pageSelect.empty", {
                  defaultValue: "No pages found",
                })}
              </MenuItem>
            )}
            {pages.map((p) => (
              <MenuItem key={p.slug} value={p.slug} className={classes.pageMenuItem}>
                {p.slug}
                {p.published ? (
                  <Chip
                    size="small"
                    label={i18n.t("siteContent.chip.published", {
                      defaultValue: "Published",
                    })}
                    style={{
                      marginInlineStart: 8,
                      background: COL.brandGreenSoft,
                      color: COL.brandGreenDeep,
                      fontWeight: 700,
                    }}
                  />
                ) : null}
              </MenuItem>
            ))}
          </TextField>

          <div className={classes.publishToggle}>
            <FormControlLabel
              control={
                <Switch
                  checked={published}
                  onChange={handleTogglePublished}
                  color="primary"
                  disabled={!selectedSlug}
                />
              }
              label={
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  {published ? (
                    <PublishedIcon style={{ fontSize: 18, color: COL.brandGreen }} />
                  ) : (
                    <DraftIcon style={{ fontSize: 18, color: COL.textSecondary }} />
                  )}
                  {published
                    ? i18n.t("siteContent.toggle.published", {
                        defaultValue: "Published",
                      })
                    : i18n.t("siteContent.toggle.draft", {
                        defaultValue: "Draft",
                      })}
                </span>
              }
            />
          </div>
        </Box>
      </Paper>

      <Paper
        elevation={0}
        className={`${classes.card} ${classes.cardStripeOffset}`}
      >
        <div
          className={classes.cardStripe}
          style={{ background: currentStripeColor }}
          aria-hidden="true"
        />

        <div className={classes.sectionHead}>
          <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 0 }}>
            <span className={classes.accentMeta}>
              {i18n.t("siteContent.section.content", {
                defaultValue: "CONTENT",
              })}
            </span>
            <h2 className={classes.sectionTitle}>
              {i18n.t("siteContent.section.contentTitle", {
                defaultValue: "Translations",
              })}
            </h2>
          </div>
        </div>

        <Tabs
          value={langIdx}
          onChange={(_e, v) => setLangIdx(v)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          classes={{ indicator: classes.tabIndicator }}
          aria-label={i18n.t("siteContent.langTabsLabel", {
            defaultValue: "Language",
          })}
          className={classes.langTabs}
        >
          {LANGUAGES.map((l) => (
            <Tab key={l.code} className={classes.langTab} label={l.label} />
          ))}
        </Tabs>

        <Box className={classes.form}>
          <TextField
            className={classes.inputSurface}
            label={i18n.t("siteContent.fields.title", { defaultValue: "Title" })}
            value={tForLang.title || ""}
            onChange={(e) => patchLang(currentLang, { title: e.target.value })}
            variant="outlined"
            size="small"
            fullWidth
            InputLabelProps={{ shrink: true }}
            inputProps={MOBILE_INPUT_PROPS}
            dir={LANGUAGES[langIdx].dir}
          />

          <TextField
            className={classes.inputSurface}
            label={i18n.t("siteContent.fields.metaTitle", {
              defaultValue: "Meta title",
            })}
            value={tForLang.metaTitle || ""}
            onChange={(e) =>
              patchLang(currentLang, { metaTitle: e.target.value })
            }
            variant="outlined"
            size="small"
            fullWidth
            InputLabelProps={{ shrink: true }}
            inputProps={{ ...MOBILE_INPUT_PROPS, maxLength: META_TITLE_MAX }}
            helperText={`${(tForLang.metaTitle || "").length}/${META_TITLE_MAX}`}
            dir={LANGUAGES[langIdx].dir}
          />

          <TextField
            className={`${classes.fullRow} ${classes.inputSurface}`}
            label={i18n.t("siteContent.fields.metaDescription", {
              defaultValue: "Meta description",
            })}
            value={tForLang.metaDescription || ""}
            onChange={(e) =>
              patchLang(currentLang, { metaDescription: e.target.value })
            }
            variant="outlined"
            size="small"
            fullWidth
            multiline
            rows={2}
            InputLabelProps={{ shrink: true }}
            inputProps={{ ...MOBILE_INPUT_PROPS, maxLength: META_DESC_MAX }}
            helperText={`${(tForLang.metaDescription || "").length}/${META_DESC_MAX}`}
            dir={LANGUAGES[langIdx].dir}
          />

          <TextField
            className={`${classes.fullRow} ${classes.bodyField} ${classes.inputSurface}`}
            label={i18n.t("siteContent.fields.body", {
              defaultValue: "Body (markdown allowed)",
            })}
            value={tForLang.body || ""}
            onChange={(e) => patchLang(currentLang, { body: e.target.value })}
            variant="outlined"
            size="small"
            fullWidth
            multiline
            rows={8}
            InputLabelProps={{ shrink: true }}
            inputProps={MOBILE_INPUT_PROPS}
            dir={LANGUAGES[langIdx].dir}
          />

          <TextField
            className={`${classes.fullRow} ${classes.inputSurface}`}
            label={i18n.t("siteContent.fields.contentBlocks", {
              defaultValue: "Content blocks (JSON)",
            })}
            value={tForLang.contentBlocksRaw || "{}"}
            onChange={(e) =>
              patchLang(currentLang, { contentBlocksRaw: e.target.value })
            }
            variant="outlined"
            size="small"
            fullWidth
            multiline
            rows={10}
            InputLabelProps={{ shrink: true }}
            inputProps={{
              ...MOBILE_INPUT_PROPS,
              style: {
                // Keep 16px on mobile so iOS Safari does NOT zoom-on-focus
                // when the user taps the JSON textarea (spec B.7 floor).
                fontSize: 16,
                fontFamily:
                  "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                lineHeight: 1.45,
              },
              spellCheck: false,
            }}
            helperText={i18n.t("siteContent.fields.contentBlocksHelp", {
              defaultValue:
                "Structured Hero / Features / Pricing blocks. Must be valid JSON. Edits to this textarea override the page body for whapiz.com renderers.",
            })}
          />
        </Box>

        <div className={classes.saveRow}>
          {isMobile ? (
            <span
              className={classes.accentMeta}
              style={{ alignSelf: "flex-start" }}
            >
              {saving
                ? i18n.t("siteContent.savingMeta", {
                    defaultValue: "SAVING",
                  })
                : i18n.t("siteContent.readyMeta", {
                    defaultValue: "READY TO SAVE",
                  })}
            </span>
          ) : null}
          <Button
            onClick={handleSave}
            className={classes.saveBtn}
            disabled={saving || !selectedSlug}
            startIcon={
              saving ? (
                <CircularProgress size={16} style={{ color: "#fff" }} />
              ) : (
                <SaveIcon />
              )
            }
          >
            {saving
              ? i18n.t("siteContent.saving", { defaultValue: "Saving…" })
              : i18n.t("siteContent.save", { defaultValue: "Save page" })}
          </Button>
        </div>
      </Paper>
    </div>
  );
};

export default PagesEditor;
